import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  Flex,
  FlexProps,
  HStack,
  ModalBodyProps,
} from "@chakra-ui/react";
import {
  ComponentProps,
  ComponentPropsWithRef,
  ElementType,
  useCallback,
  useRef,
  useState,
} from "react";
import { useDraggable } from "src/hooks/useDraggable";
import { useAutosavedState } from "src/hooks/useLocalStorage";
import { z } from "zod";

export type ResizableDrawerProps = {
  autosaveId?: string;
  contentProps?: ResizableDrawerContentProps;
  bodyProps?: ModalBodyProps;
} & DrawerProps;

export const ResizableDrawer: React.FC<ResizableDrawerProps> = ({
  children,
  autosaveId,
  contentProps,
  bodyProps,
  ...props
}) => {
  return (
    <Drawer {...props}>
      <DrawerOverlay />
      <ResizableDrawerContent
        bg="white"
        padding="0"
        gap="0"
        autosaveId={autosaveId}
        {...contentProps}
      >
        {children}
      </ResizableDrawerContent>
    </Drawer>
  );
};

type ContentProps<C extends ElementType = "div"> = {
  as?: C;
  children: React.ReactNode;
} & Omit<FlexProps, "as" | "children"> &
  Omit<ComponentPropsWithRef<C>, keyof FlexProps>;

export function Content<C extends ElementType = "div">({
  as,
  children,
  ...props
}: ContentProps<C>) {
  return (
    <Flex height="100%" direction="column" as={as} {...props}>
      {children}
    </Flex>
  );
}

type ResizableDrawerContentProps = {
  autosaveId?: string;
  minWidthPercent?: number;
  maxWidthPercent?: number;
  initialWidthPercent?: number;
} & DrawerContentProps;

// Note: This assumes that the drawer comes from the right side of the screen,
// but that can be changed if needed.
function ResizableDrawerContent({
  children,
  autosaveId,
  minWidthPercent = 20,
  maxWidthPercent = 90,
  initialWidthPercent = 30,
  ...props
}: ResizableDrawerContentProps) {
  const [width, setWidth] = useAutosavedState({
    key: autosaveId,
    schema: z
      .number()
      .transform((value) => clamp(value, minWidthPercent, maxWidthPercent)),
    initialValue: initialWidthPercent,
  });
  const { isDragging, onMouseDown } = useDraggable({
    onDragStart: useCallback(
      () =>
        ({ current }) => {
          const widthPercent = 100 - (current.x / window.innerWidth) * 100;
          setWidth(clamp(widthPercent, minWidthPercent, maxWidthPercent));
        },
      [minWidthPercent, maxWidthPercent, setWidth]
    ),
  });
  return (
    <DrawerContent {...props} maxWidth={`${width}%`}>
      <Box
        width="4px"
        height="100%"
        position="absolute"
        left={0}
        top={0}
        cursor="col-resize"
        bg={isDragging ? "gray.400" : undefined}
        _hover={{ bg: "gray.400" }}
        onMouseDown={onMouseDown}
      />
      {children}
    </DrawerContent>
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}

function useHasOverflow<T extends HTMLElement>(): [
  (node: T | null) => void,
  boolean
] {
  const ref = useRef<T | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const setRef = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    ref.current = node;
    if (!node) return;

    const check = () => setHasOverflow(node.scrollHeight > node.clientHeight);

    const observer = new ResizeObserver(check);
    observer.observe(node);
    for (const child of Array.from(node.children)) {
      observer.observe(child);
    }
    observerRef.current = observer;
    check();
  }, []);

  return [setRef, hasOverflow];
}

type HeaderProps = {
  children: React.ReactNode;
  showCloseButton?: boolean;
  onCloseButtonClick?: () => void;
} & ComponentProps<typeof DrawerHeader>;
export const Header: React.FC<HeaderProps> = ({
  children,
  showCloseButton = true,
  onCloseButtonClick,
  ...props
}) => {
  return (
    <DrawerHeader {...props}>
      <HStack
        justify="space-between"
        align="top"
        paddingX="6"
        paddingTop="4"
        paddingBottom="2"
        fontSize="xl"
        fontWeight="bold"
      >
        <Box>{children}</Box>
        {showCloseButton && <DrawerCloseButton onClick={onCloseButtonClick} />}
      </HStack>
    </DrawerHeader>
  );
};

type FooterProps = { children: React.ReactNode } & ComponentProps<
  typeof DrawerFooter
>;
export const Footer: React.FC<FooterProps> = ({ children, ...props }) => {
  return (
    <DrawerFooter
      p="4"
      display="flex"
      gap="2"
      sx={{
        "& > *": {
          flex: "1", // automatically grow all children, can be overriden by children
        },
      }}
      {...props}
    >
      {children}
    </DrawerFooter>
  );
};

type BodyProps = { children: React.ReactNode } & React.ComponentProps<
  typeof DrawerBody
>;
export const Body: React.FC<BodyProps> = ({ children, ...bodyProps }) => {
  const [bodyRef, hasOverflow] = useHasOverflow<HTMLDivElement>();
  return (
    <DrawerBody
      paddingX="6"
      ref={bodyRef}
      borderTop={hasOverflow ? "1px solid" : "none"}
      borderBottom={hasOverflow ? "1px solid" : "none"}
      borderColor="gray.300"
      {...bodyProps}
    >
      {children}
    </DrawerBody>
  );
};
